import React from 'react';
import styled from 'styled-components';
import Modal, { Prompt, ModalFooter, ModalHeader } from '@ingka/modal';
import Button from '@ingka/button';
import Text from '@ingka/text';
import { mediaQueries } from '../styles/mediaQueries';
import useTranslation from '../hooks/useTranslation';
import ServicePrice from './ServicePrice';
import { isBusinessOperation } from '../helpers/urlHelper';
import { ReplacementService } from '../services/Appointment/model';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  ${mediaQueries.portraitTabletUp} {
    flex-direction: row;
  }
`;

const LeftContent = styled.div`
  flex: 1 1 auto;
  word-break: break-word;
  hyphens: auto;
  -moz-hyphens: auto;
  -webkit-hyphens: auto;
`;

const RightContent = styled.div`
  flex: 1 0 auto;
  margin-top: 12px;

  ${mediaQueries.portraitTabletUp} {
    margin-left: 1.5rem;
    margin-top: 0;
    justify-content: flex-end;
  }
`;

interface SelectReplacementServiceConfirmationProps {
  showModal: boolean;
  onContinueButtonClick: () => void;
  onCloseButtonClick: () => void;
  replacementService: ReplacementService;
  selectedOriginalServiceInclSavingsPrice?: number;
  selectedOriginalServiceExclSavingsPrice?: number;
}

const SelectReplacementServiceConfirmation: React.FC<
  SelectReplacementServiceConfirmationProps
> = ({
  onContinueButtonClick,
  onCloseButtonClick,
  showModal,
  replacementService,
  selectedOriginalServiceInclSavingsPrice,
  selectedOriginalServiceExclSavingsPrice,
}) => {
  const { t } = useTranslation();
  const isBusiness = isBusinessOperation(replacementService.operation);
  const footer = (
    <ModalFooter>
      <Button
        text={t(
          'components.selectReplacementServiceConfirmation.continueButton',
        )}
        type="primary"
        data-cy="confirmation-modal-button"
        onClick={onContinueButtonClick}
      />
    </ModalFooter>
  );
  const isPriceDifferent =
    replacementService.displayPrice.inclSavings !==
      selectedOriginalServiceInclSavingsPrice ||
    selectedOriginalServiceExclSavingsPrice !==
      replacementService.displayPrice.exclSavings;
  return (
    <Modal visible={showModal} handleCloseBtn={onCloseButtonClick}>
      <Prompt
        titleId="replacementServiceBookingConfirmation"
        title=""
        footer={footer}
        header={
          <ModalHeader
            ariaCloseTxt={t(
              'components.selectReplacementServiceConfirmation.closeButton',
            )}
          />
        }
      >
        <Container>
          <LeftContent>
            <Text textStyle="Body.L">
              {t('components.selectReplacementServiceConfirmation.description')}
            </Text>
            <Text textStyle="Body.L">
              <strong>
                {t(`services.name.${replacementService.serviceId}`)}
              </strong>
            </Text>
            {t(`components.servicePicker.${replacementService.location}`)}
          </LeftContent>
          {isPriceDifferent && (
            <RightContent>
              <ServicePrice
                price={replacementService.displayPrice}
                serviceId={replacementService.serviceId}
                isBusinessOperation={isBusiness}
              />
            </RightContent>
          )}
        </Container>
      </Prompt>
    </Modal>
  );
};

export default SelectReplacementServiceConfirmation;
